import type { TFunction } from 'i18next'

export type TradeCloseSummaryInput = {
  symbol: string
  strategy: string | null
  longShort: 'long' | 'short'
  entryPrice: number
  exitPrice: number | null
  averageCost: number
  stopLoss: number
  takeProfit: number
  plannedRr: number | null
  realizedRr: number | null
  pnl: number | null
  pnlPercentage: number | null
  heldDays: number | null
  addCount: number
  stoppedOut: boolean
  currency: string | null
}

function formatMoney(value: number | null, currency: string | null) {
  if (value == null || Number.isNaN(value)) return '—'
  const formatted = value.toFixed(2).replace('.', ',')
  return currency ? `${formatted} ${currency}` : formatted
}

export function buildTradeCloseAiSummary(input: TradeCloseSummaryInput, t: TFunction<'trading'>): string {
  const win = input.pnl != null && input.pnl > 0
  const loss = input.pnl != null && input.pnl < 0
  const pct =
    input.pnlPercentage != null
      ? `${input.pnlPercentage >= 0 ? '+' : ''}${input.pnlPercentage.toFixed(2)}%`
      : null

  const outcome = win
    ? t('tradeDetail.studio.aiSummary.outcomeWin')
    : loss
      ? t('tradeDetail.studio.aiSummary.outcomeLoss')
      : t('tradeDetail.studio.aiSummary.outcomeFlat')

  const closeReason = input.stoppedOut
    ? t('tradeDetail.studio.aiSummary.reasonStopped')
    : win && input.exitPrice != null && input.takeProfit > 0
      && Math.abs(input.exitPrice - input.takeProfit) / input.takeProfit < 0.02
      ? t('tradeDetail.studio.aiSummary.reasonTp')
      : t('tradeDetail.studio.aiSummary.reasonClosed')

  const held =
    input.heldDays != null
      ? t('tradeDetail.studio.aiSummary.heldDays', { count: input.heldDays })
      : ''

  const scaling =
    input.addCount > 0
      ? t('tradeDetail.studio.aiSummary.scalingAdds', { count: input.addCount })
      : t('tradeDetail.studio.aiSummary.scalingSingle')

  const rr =
    input.realizedRr != null
      ? t('tradeDetail.studio.aiSummary.realizedRr', {
          value: `${input.realizedRr >= 0 ? '+' : ''}${input.realizedRr.toFixed(2)}R`,
        })
      : input.plannedRr != null
        ? t('tradeDetail.studio.aiSummary.plannedRr', { value: `${input.plannedRr}:1` })
        : ''

  const strategy = input.strategy
    ? t('tradeDetail.studio.aiSummary.strategy', { name: input.strategy })
    : ''

  const resultLine = t('tradeDetail.studio.aiSummary.resultLine', {
    outcome,
    pnl: formatMoney(input.pnl, input.currency),
    pct: pct ?? '—',
    closeReason,
  })

  const levelsLine = t('tradeDetail.studio.aiSummary.levelsLine', {
    entry: formatMoney(input.entryPrice, input.currency),
    avg: formatMoney(input.averageCost, input.currency),
    exit: formatMoney(input.exitPrice, input.currency),
    sl: formatMoney(input.stopLoss, input.currency),
  })

  const parts = [resultLine, levelsLine, held, scaling, rr, strategy].filter(Boolean)
  return parts.join(' ')
}
